import { PatientReferenceIdentifier } from '@dreryk/egabinet-patient-published-language-api';

import { PatientStatus, StatusIconTooltip, Syncable, TooltipPlacement } from '@dreryk/react-components';

import {
   PatientEntitlementToMedicalServiceStatusVerification,
   useGetPatientEntitlementToMedicalServiceStatus,
} from '~libs.shared.patient-entitlement-to-medical-service';
import { VisitId } from '~libs.shared.visit';

import { usePatientEntitlementToMedicalServiceStatusOptions } from './shared/usePatientEntitlementToMedicalServiceStatusOptions';
import { getPatientEntitlementToMedicalServiceStatusTooltipText } from './shared/getPatientEntitlementToMedicalServiceStatusTooltipText';
import { dispatchEwusPasswordDaysUntilExpirationSnackbarEvent } from './shared/dispatchEwusPasswordDaysUntilExpirationSnackbarEvent';
import { dispatchEwusPasswordExpiredSnackbarEvent } from './shared/dispatchEwusPasswordExpiredSnackbarEvent';
import { translationKeys } from './translations';
import { useEwusLoginModal } from './ewusLogin/useEwusLoginModal';

export type PatientEntitlementToMedicalServiceStatusProps = {
   readonly visitId: VisitId;
   readonly checkDate: Date;
   readonly readonly: boolean;
   readonly patientReferenceIdentifier: PatientReferenceIdentifier | undefined;
};

export const PatientEntitlementToMedicalServiceStatus = (
   props: PatientEntitlementToMedicalServiceStatusProps
): JSX.Element => {
   const options = usePatientEntitlementToMedicalServiceStatusOptions();
   const patientEntitlementToMedicalServiceStatus = useGetPatientEntitlementToMedicalServiceStatus(props.visitId);
   const { openEwusLoginModal, renderEwusLoginModal } = useEwusLoginModal();

   return (
      <PatientEntitlementToMedicalServiceStatusVerification
         visitId={props.visitId}
         checkDate={props.checkDate}
         readonly={props.readonly}
         patientReferenceIdentifier={props.patientReferenceIdentifier}
         initialStatus={patientEntitlementToMedicalServiceStatus}
         onEwusPasswordDaysUntilExpirationFault={dispatchEwusPasswordDaysUntilExpirationSnackbarEvent}
         onEwusPasswordExpiredFault={dispatchEwusPasswordExpiredSnackbarEvent}
         onEwusLoginRequired={openEwusLoginModal}
         verifyOnMount
      >
         {({ verifyStatus, state }) => {
            const tooltipText = getPatientEntitlementToMedicalServiceStatusTooltipText(state);

            return (
               <>
                  <StatusIconTooltip
                     tooltipText={tooltipText}
                     tooltipPlacement={TooltipPlacement.TOP}
                     testId={'ewusStatus'}
                  >
                     <Syncable inactive={!state.verifyAvailable} onClick={verifyStatus} testId={'ewusStatus'}>
                        <PatientStatus
                           options={options}
                           type={state.status}
                           label={
                              state.status === 'VALID_ENTITLEMENT_STATEMENT'
                                 ? translationKeys.entitlementStatement
                                 : translationKeys.ewus
                           }
                           testId={'ewus'}
                        />
                     </Syncable>
                  </StatusIconTooltip>

                  {renderEwusLoginModal({
                     onLogin: verifyStatus,
                  })}
               </>
            );
         }}
      </PatientEntitlementToMedicalServiceStatusVerification>
   );
};
